import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { loginDoModulo, rotaDeLogin } from '@core/auth/auth.guard';
import { AuthService } from '@core/auth/auth.service';
import { EntidadeContextoService } from '@core/contexto/entidade-contexto.service';

/**
 * Anexa "Authorization: Bearer <token>" às requisições destinadas ao gdic-api
 * (exceto às rotas públicas de autenticação) e encerra a sessão local quando
 * o backend responde 401 (token expirado/invalidado).
 *
 * Registrado em app.config.ts via provideHttpClient(withInterceptors(...)).
 */
export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const entidadeContexto = inject(EntidadeContextoService);
  const paraApi = req.url.startsWith(environment.backend);
  const ehLogin = req.url.includes('/api/v1/auth/');
  const token = auth.token();

  if (paraApi && token && !ehLogin) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    catchError((erro: unknown) => {
      // O 401 do próprio login é credencial inválida, e quem trata é a tela de login.
      if (paraApi && !ehLogin && erro instanceof HttpErrorResponse && erro.status === 401) {
        // A entidade é lida ANTES do logout, que zera as claims; o contexto guardado pela tela
        // de login é a rede para o token que não traz `tenantId`. Sem ela na URL, a sessão que
        // expira obriga a escolher a entidade de novo — mesmo problema do logout pelo menu.
        const idTenancy = auth.usuario()?.tenantId ?? entidadeContexto.idTenancy();
        auth.logout();
        // Sem isto o usuário ficava na tela, com a sessão já descartada, vendo o erro do
        // backend a cada ação — em vez de ser levado a entrar de novo. A URL corrente volta
        // como returnUrl para que ele retome de onde parou.
        //
        // A checagem evita que requisições concorrentes (uma tela que carrega em paralelo e
        // recebe vários 401) reescrevam o returnUrl apontando para o próprio login.
        const url = router.url;
        if (!url.includes('/login')) {
          router.navigate(rotaDeLogin(loginDoModulo(url), idTenancy),
            { queryParams: { returnUrl: url } });
        }
      }
      return throwError(() => erro);
    })
  );
};
